import React, { useEffect, useState } from "react";
import { useAppServices } from "../../../hook/services";
import { useParams } from "react-router-dom";
import LoadingScreen from "../../../components/LoadingScreen";

const ITEMS_PER_PAGE = 9;

const MessageLogs = () => {
  const AppServices = useAppServices();
  const { locationId } = useParams();

  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    const filtered =
      statusFilter === "all"
        ? logs
        : logs.filter((log) => log.status === statusFilter);
    setFilteredLogs(filtered);
    setCurrentPage(1);

    setSelected((prev) => {
      const newSet = new Set(
        [...prev].filter((id) => {
          const log = logs.find((l) => l._id === id);
          return log?.status === "failed";
        })
      );
      return newSet;
    });
  }, [logs, statusFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { response } = await AppServices.message.get({
        query: `locationId=${locationId}`,
      });
      if (response) {
        setLogs(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch logs", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (logId) => {
    const log = logs.find((l) => l._id === logId);
    if (log.status !== "failed") return;
    const updated = new Set(selected);
    updated.has(logId) ? updated.delete(logId) : updated.add(logId);
    setSelected(updated);
  };

  const handleSelectAll = () => {
    const failedIds = filteredLogs
      .filter((log) => log.status === "failed")
      .map((log) => log._id);
    setSelected(new Set(failedIds));
  };

  const clearSelection = () => {
    setSelected(new Set());
  };

  const handleDelete = async (ids) => {
    setActionLoading(true);
    try {
      const payload = { ids: Array.from(ids) };
      const { response } = await AppServices.message.bulk_delete({ payload });
      if (response) {
        setSelected(new Set());
        fetchLogs();
      }
    } catch (err) {
      console.error("Failed to delete logs", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResend = async (ids) => {
    setActionLoading(true);
    try {
      const payload = {
        ids: Array.from(ids),
        account_id: locationId,
      };
      const { response } = await AppServices.message.bulk_resend({ payload });
      if (response) {
        fetchLogs();
        setSelected(new Set());
      }
    } catch (err) {
      console.error("Failed to resend", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  if (loading) {
    return <LoadingScreen loadingText={"Loading logs..."} />;
  }

  return (
    <div className="container mt-4">
      <h4 className="mb-4">📨 Message Logs</h4>

      {/* Filter + Actions */}
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 p-3 mb-4 rounded shadow-sm bg-white border">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <label
            htmlFor="statusFilter"
            className="form-label fw-semibold mb-0 me-2"
          >
            Filter Status:
          </label>
          <select
            id="statusFilter"
            className="form-select form-select-sm w-auto"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <small className="text-muted">{selected.size} selected</small>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleSelectAll}
            disabled={
              filteredLogs.filter((log) => log.status === "failed").length === 0
            }
          >
            Select All Failed
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={clearSelection}
            disabled={selected.size === 0}
          >
            Clear Selection
          </button>
          <button
            className="btn btn-sm btn-primary"
            disabled={selected.size === 0 || actionLoading}
            onClick={() => handleResend(selected)}
          >
            {actionLoading ? "Resending..." : "Resend"}
          </button>
          <button
            className="btn btn-sm btn-danger"
            disabled={selected.size === 0 || actionLoading}
            onClick={() => handleDelete(selected)}
          >
            {actionLoading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>

      {/* Message Cards */}
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <p>No message logs found for selected filter.</p>
      ) : (
        <>
          <div className="row g-3">
            {paginatedLogs.map((log) => (
              <div className="col-md-6 col-lg-4" key={log._id}>
                <div className="card h-100 shadow-sm">
                  <div className="card-body position-relative">
                    {log.status === "failed" && (
                      <div className="form-check position-absolute top-0 end-0 p-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selected.has(log._id)}
                          onChange={() => toggleSelect(log._id)}
                        />
                      </div>
                    )}
                    <h6 className="card-title mb-1">
                      {log?.to || "Unknown Number"}
                    </h6>
                    <p className="card-text text-muted mb-2 small">
                      {log.messageContent || "[No Content]"}
                    </p>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <span
                        className={`badge bg-${
                          log.status === "sent" ? "success" : "danger"
                        }`}
                      >
                        {log.status}
                      </span>
                      <span
                        className={`badge bg-${
                          log.direction === "inbound" ? "info" : "warning"
                        }`}
                      >
                        {log.direction}
                      </span>
                    </div>
                    <small className="text-muted d-block mt-2">
                      SIM Slot: {log.simSlot} | Device: {log.deviceId}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    &laquo;
                  </button>
                </li>

                {Array.from({ length: totalPages }).map((_, index) => (
                  <li
                    key={index}
                    className={`page-item ${
                      currentPage === index + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(index + 1)}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default MessageLogs;
